import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUnit } from '../../Session';
import LibraryCardButton from '../LibraryCardButtons/LibraryCardButton';
import { getPublicMediaRoot } from '../../serviceAgent';
import { fixedEncodeURIComponent, getLetterGradeFromNumberGrade } from '../../GlobalFunctions';
import { LibraryCardProps, ElementaryCardData, ElementaryLessonCardProps, SecondaryLessonCardProps } from './cardData';
import cx from 'classnames';
import styles from './LibraryCard.module.scss';

type Props = LibraryCardProps & ElementaryLessonCardProps & ElementaryCardData & SecondaryLessonCardProps;

const LessonLibraryCard = ({
  displayName,
  subtitle,
  index,
  label,
  content,
  buttonText,
  unit,
  classroom,
  isShow,
  lessonIndex,
  moduleIndex,
  lessonType,
  slug,
  featuredImage,
  esFeaturedImage,
  hasSpanishContent,
}: Props): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { grade, className } = classroom;

  const isElementary = grade < 6;
  const isMiddleSchool = grade >= 6 && grade < 9;
  const isHighSchool = grade >= 9 && grade < 13;

  let unitName = '';
  if (unit === 'identity-standard') {
    unitName = 'identity';
  } else if (unit === 'hsidentity-standard') {
    unitName = 'hsidentity';
  } else {
    unitName = unit;
  }

  const getUnitThumbnailName = () => {
    let unitThumbnailName = '';
    if (unit === 'Imagination' && grade > 2) {
      unitThumbnailName = 'ImaginationStation';
    } else if (isMiddleSchool) {
      unitThumbnailName = unitName.charAt(0).toUpperCase() + unitName.slice(1);
    } else if (isHighSchool) {
      unitThumbnailName = unitName.slice(2).charAt(0).toUpperCase() + unitName.slice(3) + 'HS';
    } else {
      unitThumbnailName = unitName;
    }
    return unitThumbnailName;
  };

  const getThumbnail = () => {
    let imgPath = '';
    if (isElementary) {
      switch (label) {
        case 'Letter':
          imgPath = `${getPublicMediaRoot()}images/thumbnails/elementaryLessons/letters/${getUnitThumbnailName()}-${label}.png`;
          break;
        case 'Storybook':
          imgPath =
            classroom.language == 'en'
              ? `${getPublicMediaRoot()}images/thumbnails/${getUnitThumbnailName()}Bookshelf.png`
              : `${getPublicMediaRoot()}images/thumbnails/es${getUnitThumbnailName()}Bookshelf.png`;
          break;
      }
    } else {
      switch (label) {
        case 'Activity':
          imgPath = `${getPublicMediaRoot()}images/thumbnails/secondaryModules/${unitName}/${getUnitThumbnailName()}-G${grade}-L${lessonIndex}-M${moduleIndex}.png`;
          break;
      }
    }
    return imgPath;
  };

  const getUrl = () => {
    let url = '';
    const readingLevel = classroom.readingLevel.toString();
    if (isElementary) {
      switch (label) {
        case 'Letter':
          url = `/${fixedEncodeURIComponent(className)}/lessonPlan/readAloud/${unit}-${getLetterGradeFromNumberGrade(
            grade,
          )}/${unit}-${getLetterGradeFromNumberGrade(
            grade,
          )}-readAloud-${index}/Subunit${index}/Lesson${index}/Step${index}`;
          break;
        case 'Storybook':
          url = slug
            ? `/${fixedEncodeURIComponent(className)}${slug}`
            : `/story/${fixedEncodeURIComponent(unit)}-${fixedEncodeURIComponent(readingLevel)}`;
          break;
      }
    } else {
      switch (label) {
        case 'Activity':
          url = `/lessonPlan/${unit}/en/${grade}/lesson/${lessonType}/activity/${unitName}-${slug}`;
          break;
      }
    }
    return url;
  };

  const handleClick = () => {
    dispatch(setUnit(unit));
    const url = getUrl();
    history.push(url);
  };

  return isShow ? (
    <div className={styles.container}>
      <div className={styles.imgContainer}>
        <img
          className={cx({
            [styles.lessonLibraryCardThumbnail]: isMiddleSchool,
            [styles.lessonLibraryCardThumbnailHS]: isHighSchool,
          })}
          src={getThumbnail()}
          alt={displayName}
          onClick={handleClick}
        />
      </div>
      <div className={styles.textContainer}>
        <h3 className={styles.typeHeading}>{subtitle && subtitle}</h3>
        <div className={styles.titleRow}>
          <h2 className={styles.unitTitle}>{displayName && displayName}</h2>
        </div>
        <p>{content}</p>
        <div className={styles.buttonRow}>
          <>
            <LibraryCardButton buttonText={buttonText || 'Explore'} handleClick={handleClick} />
          </>
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};

export default LessonLibraryCard;
